import KEYCLOAK_CONFIG from "./keycloakConfig";

// ── Tek bir mutex: hem hook hem interceptor bu servisi kullanır ──
let isRefreshing = false;
let refreshPromise = null;

// JWT token'ın exp claim'ini parse et
function getTokenExp(token) {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const base64 =
      parts[1].replace(/-/g, "+").replace(/_/g, "/") +
      "==".slice(2 - (parts[1].length % 4));
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join(""),
    );
    return JSON.parse(json).exp || null;
  } catch {
    return null;
  }
}

// Token'ın süresi dolmuş mu veya dolmak üzere mi (margin saniye kala)
export function isTokenExpired(token, marginSeconds = 30) {
  if (!token) return true;
  const exp = getTokenExp(token);
  if (!exp) return true;
  return Date.now() / 1000 > exp - marginSeconds;
}

// ── Silent Re-Authentication (iframe ile prompt=none) ──
function silentReAuthenticate() {
  return new Promise((resolve, reject) => {
    const state = Math.random().toString(36).substring(2);
    const redirectUri = KEYCLOAK_CONFIG.silentRedirectUri;

    const authUrl =
      `${KEYCLOAK_CONFIG.realmUrl}/protocol/openid-connect/auth` +
      `?client_id=${encodeURIComponent(KEYCLOAK_CONFIG.clientId)}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}` +
      `&response_type=code` +
      `&scope=openid` +
      `&prompt=none` +
      `&state=${state}`;

    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    document.body.appendChild(iframe);

    const timeout = setTimeout(() => {
      cleanup();
      reject(new Error("Silent auth timeout"));
    }, 10000);

    function cleanup() {
      clearTimeout(timeout);
      window.removeEventListener("message", onMessage);
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
    }

    function onMessage(event) {
      if (event.source !== iframe.contentWindow) return;
      if (event.origin !== window.location.origin) return;
      if (event.data?.state !== state) return;
      if (event.data?.type === "keycloak-silent-auth") {
        cleanup();
        if (event.data.code) {
          resolve(event.data.code);
        } else {
          reject(new Error(event.data.error || "Silent auth failed"));
        }
      }
    }

    window.addEventListener("message", onMessage);
    iframe.src = authUrl;
  });
}

// Authorization code → token
async function exchangeCodeForTokens(code, redirectUri) {
  const params = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: KEYCLOAK_CONFIG.clientId,
    code: code,
    redirect_uri: redirectUri,
  });

  const response = await fetch(
    `${KEYCLOAK_CONFIG.realmUrl}/protocol/openid-connect/token`,
    {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: params,
    },
  );

  return response.json();
}

// Token exchange (DefaultClient → company client)
async function tokenExchangeForClient(subjectToken, companyName) {
  const params = new URLSearchParams({
    grant_type: "urn:ietf:params:oauth:grant-type:token-exchange",
    client_id: KEYCLOAK_CONFIG.clientId,
    subject_token: subjectToken,
    audience: companyName,
    requested_token_type: "urn:ietf:params:oauth:token-type:access_token",
  });

  const response = await fetch(
    `${KEYCLOAK_CONFIG.realmUrl}/protocol/openid-connect/token`,
    {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: params,
    },
  );

  return response.json();
}

// Token exchange yardımcı — hem normal refresh hem silent re-auth sonrası kullanılır
async function applyTokenExchange(accessToken, refreshToken, companyName) {
  if (refreshToken) {
    localStorage.setItem("refreshToken", refreshToken);
  }

  if (companyName && companyName !== KEYCLOAK_CONFIG.clientId) {
    const exchangeData = await tokenExchangeForClient(accessToken, companyName);
    if (exchangeData.access_token) {
      localStorage.setItem("authToken", exchangeData.access_token);
      if (exchangeData.refresh_token) {
        localStorage.setItem("refreshToken", exchangeData.refresh_token);
      }
      return exchangeData.access_token;
    }
    // Exchange başarısız olsa da DefaultClient token'ını kullan
    localStorage.setItem("authToken", accessToken);
    return accessToken;
  }

  localStorage.setItem("authToken", accessToken);
  return accessToken;
}

// Silent re-auth flow
async function handleSilentReAuth(companyName) {
  const code = await silentReAuthenticate();
  const tokenData = await exchangeCodeForTokens(
    code,
    KEYCLOAK_CONFIG.silentRedirectUri,
  );

  if (!tokenData.access_token) {
    throw new Error("No access_token from silent auth");
  }

  if (tokenData.id_token) {
    localStorage.setItem("idToken", tokenData.id_token);
  }

  return applyTokenExchange(
    tokenData.access_token,
    tokenData.refresh_token,
    companyName,
  );
}

/**
 * Merkezi token yenileme fonksiyonu.
 * - Zaten refresh yapılıyorsa aynı promise'i döndürür (mutex).
 * - Refresh token geçerliyse normal refresh yapar.
 * - Refresh token expired ise silent re-auth dener.
 * - Her şey başarısızsa null döner (logout kararı çağırana bırakılır).
 *
 * @returns {Promise<string|null>} Yeni access token veya null
 */
export async function refreshAccessToken() {
  // Zaten bir refresh devam ediyorsa, aynı promise'i bekle
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  const refreshToken = localStorage.getItem("refreshToken");
  const companyName = localStorage.getItem("companyName");

  if (!refreshToken) {
    return null;
  }

  isRefreshing = true;

  refreshPromise = (async () => {
    try {
      // 1) Refresh token hala geçerliyse normal refresh
      if (!isTokenExpired(refreshToken, 30)) {
        const params = new URLSearchParams({
          grant_type: "refresh_token",
          client_id: KEYCLOAK_CONFIG.clientId,
          refresh_token: refreshToken,
        });

        const response = await fetch(
          `${KEYCLOAK_CONFIG.realmUrl}/protocol/openid-connect/token`,
          {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: params,
          },
        );

        const data = await response.json();

        if (data.access_token) {
          return await applyTokenExchange(
            data.access_token,
            data.refresh_token,
            companyName,
          );
        }

        // invalid_grant → refresh token sunucu tarafında geçersiz, silent re-auth dene
        if (data.error === "invalid_grant") {
          return await handleSilentReAuth(companyName);
        }

        return null;
      }

      // 2) Refresh token expired → silent re-auth
      return await handleSilentReAuth(companyName);
    } catch {
      return null;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}
